import { useState } from 'react';
import { ChevronDown, Plus, Check } from 'lucide-react';

export default function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
  count,
  checkedCount,
  iconColor = 'text-accent',
  onAdd
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const isComplete = checkedCount !== undefined && checkedCount === count && count > 0;
  const hasProgress = checkedCount !== undefined && count !== undefined;

  const getAccentClass = () => {
    if (iconColor.includes('weekly')) return 'weekly';
    if (iconColor.includes('monthly')) return 'monthly';
    return 'accent';
  };

  const accent = getAccentClass();

  return (
    <div className="mb-3">
      {/* Section Header */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            flex-1 flex items-center gap-2.5 p-2.5
            bg-surface-card/60 hover:bg-surface-card
            rounded-xl border border-border hover:border-border-strong
            shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-soft)]
            cursor-pointer transition-all duration-200
          `}
        >
          {/* Icon container */}
          <div className={`
            p-1.5 rounded-md
            ${isComplete ? 'bg-success-light' :
              accent === 'weekly' ? 'bg-weekly-light' :
              accent === 'monthly' ? 'bg-monthly-light' :
              'bg-accent-muted'}
          `}>
            {isComplete ? (
              <Check size={12} className="text-success" strokeWidth={2.5} />
            ) : (
              Icon && <Icon size={12} className={iconColor} />
            )}
          </div>

          {/* Title & counts */}
          <div className="flex-1 text-left">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-xs text-text-primary">{title}</span>

              {hasProgress && (
                <span className={`
                  px-1.5 py-0.5 rounded text-[10px] font-semibold
                  ${isComplete ? 'bg-success-light text-success' :
                    'bg-surface-hover text-text-secondary'}
                `}>
                  {checkedCount}/{count}
                </span>
              )}

              {!hasProgress && count !== undefined && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-hover text-text-tertiary">
                  {count}
                </span>
              )}

              {isComplete && (
                <span className="text-[10px] text-success font-medium">Done</span>
              )}
            </div>
          </div>

          <ChevronDown
            size={14}
            className={`
              text-text-tertiary transition-transform duration-200
              ${isOpen ? 'rotate-0' : '-rotate-90'}
            `}
          />
        </button>

        {onAdd && (
          <button
            onClick={onAdd}
            className="p-2 rounded-lg bg-surface-card/50 hover:bg-surface-card border border-border hover:border-accent/50 text-text-tertiary hover:text-accent cursor-pointer transition-all duration-200"
            title={`Add ${title.toLowerCase()}`}
          >
            <Plus size={14} />
          </button>
        )}
      </div>

      {/* Section Content */}
      <div className={`
        overflow-hidden transition-all duration-300 ease-out
        ${isOpen ? 'opacity-100' : 'opacity-0 max-h-0'}
      `}>
        <div className="space-y-1.5">
          {children}
        </div>
      </div>
    </div>
  );
}
